<template lang="html">
  <div v-if="dataBound">
    <b-container v-if="!timelineFocusLayout" :fluid="false">
      <TopBar class="mb-5" />
      <b-row v-if="displayInitialImporter">
        <b-col>
          <ImporterInitial />
        </b-col>
      </b-row>

      <b-row v-if="!displayInitialImporter && !minimalMediaDeployed">
        <b-col>
          <div class="d-flex justify-content-center mb-3 mt-5">
            <h4>
              Deploying new assets to <WorldIcon /> content delivery network ...
            </h4>
          </div>
          <div class="d-flex justify-content-center mb-3 mt-5">
            <b-spinner
              style="width: 3rem; height: 3rem;"
              type="grow"
              size="lg"
              variant="primary"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
    <b-container>
      <!-- Post initial import upper edit, with audio -->
      <div
        v-if="safeForAllModes && showHasAudio"
        :class="{
          'absolute absolute-row': timelineFocusLayout,
          row: !timelineFocusLayout
        }"
      >
        <div :class="{ 'col-md-6': !timelineFocusLayout }">
          <!-- Headline & Preview -->
          <ModeBar v-if="!timelineFocusLayout" class="mb-4" />
          <div
            :style="embedContainerStyle"
            :class="{
              'absolute-player': timelineFocusLayout
            }"
            class="embed-container"
          >
            <SoundslidesPlayer
              v-if="dataBound"
              :key="'player_' + $store.state.show.remountId"
              ref="editPlayer"
              :editing_initial_slide_data="show.data.slideArray"
              :editing_initial_overlay_data="show.data.overlayArray"
              :editing_initial_settings_data="show.data.displaySettings"
              :editing_audio_source_url="audioUrl"
              :editing_audio_duration="show.data.audio.duration"
              :editable_overlays="$store.state.show.editableOverlays"
              :force_preload="true"
              :fillscreen="false"
              :always_show_controls="timelineFocusLayout"
              :capture-and-emit-click="captureAndEmitClick"
              @onSlideChange="slideChange"
              @onTimeUpdate="timeUpdate"
              @onMounted="onPlayerMounted"
              @clickCaptured="clickCaptured"
              @mouseDownCaptured="mouseDownCaptured"
            />
          </div>
        </div>
        <b-col v-show="!timelineFocusLayout">
          <!-- Simple & Precise Setup -->
          <ShowSettings v-if="mode === 'settings'" />
          <ShowOverlays
            v-if="mode === 'overlays'"
            :current-time="currentTime"
            @gotoAndPause="gotoAndPause"
          />
          <ShowExport v-if="mode === 'export'" />
          <ShowEmbed v-if="mode === 'embed'" />
          <ShowTimelineSetup
            v-if="mode === 'timeline' && !isCurrentShowTimelineEdited"
          />
          <ShowTimelineExists
            v-if="mode === 'timeline' && isCurrentShowTimelineEdited"
          />
          <div v-if="newTimelineMode">
            <SlideInspector v-if="activeItemType == 'slide'" />
            <OverlayInspector v-if="activeItemType == 'overlay'" />
            <!-- Focus upper container -->
            <p v-if="false">Active item: {{ $store.state.show.activeItem }}</p>
            <p v-if="false">
              Selected list: {{ $store.getters["show/selectedItems"] }}
            </p>
          </div>
          <QuickKeys @deleteKey="onDeleteKey" />
        </b-col>
      </div>

      <!-- Post initial import upper edit, without audio -->
      <b-row v-if="safeForAllModes && !showHasAudio">
        <b-col md="6">
          <!-- Headline & Preview -->
          <ModeBar />
          <LivePreviewNoAudio class="mt-4" />
          <div class="mt-5 alert alert-info">
            <h5>Legacy non-audio shows are read-only.</h5>
            <p class="mt-3">
              Legacy non-audio shows are limited to hosting and export functions
              only. Set the
              <b-link
                :to="{
                  name: 'show-mode',
                  params: {
                    id: show.key,
                    mode: 'settings'
                  }
                }"
              >
                privacy
              </b-link>
              of your show, and get your show's link and
              <b-link
                :to="{
                  name: 'show-mode',
                  params: {
                    id: show.key,
                    mode: 'embed'
                  }
                }"
              >
                embed code.
              </b-link>
              Or
              <b-link
                :to="{
                  name: 'show-mode',
                  params: {
                    id: show.key,
                    mode: 'export'
                  }
                }"
              >
                export
              </b-link>
              your show to a video file.
            </p>
            <p>
              For more information, please see
              <b-link
                href="https://help.soundslides.com/guide/migrating.html"
                target="_blank"
              >
                Migrating from Soundslides 1
              </b-link>
              topic on the help site.
            </p>
          </div>
        </b-col>
        <b-col>
          <!-- Settings and Embed only for non-audio slide show.  -->
          <ShowSettings v-if="mode === 'settings'" />
          <ShowEmbed v-if="mode === 'embed'" />
          <ShowExport v-if="mode === 'export'" />
        </b-col>
      </b-row>
    </b-container>

    <!-- Drag resizer for vertical split. -->
    <div
      v-if="newTimelineMode"
      v-dragged="onVResizerDragged"
      :style="vResizerStyle"
      class="v-resizer"
    />

    <!-- Post initial import upper edit, with audio -->
    <b-container
      v-if="newTimelineMode"
      :fluid="$store.state.fluidTimeline"
      :class="{
        'fullscreen-timeline': timelineFocusLayout
      }"
    >
      <!-- New Timeline Mode -->
      <div class="row" :style="timelineStyle">
        <div class="col">
          <Timeline
            v-if="playerMounted"
            ref="editTimeline"
            :player_ref="$refs.editPlayer"
            @refreshPlayerData="refreshPlayerData"
          />
        </div>
      </div>
    </b-container>

    <div
      v-show="timelineFocusLayout"
      :style="upperRightPaneStyle"
      class="upper-right-pane"
    >
      <!-- Focus upper container -->
      <SlideInspector v-if="activeItemType == 'slide'" />
      <OverlayInspector v-if="activeItemType == 'overlay'" />
      <!-- Focus upper container -->
      <p>Active item: {{ $store.state.show.activeItem }}</p>
      <p>Selected list: {{ $store.getters["show/selectedItems"] }}</p>
    </div>

    <b-container :fluid="false">
      <!-- Post initial import lower edit -->
      <b-row v-if="safeForAllModes">
        <b-col>
          <ImporterAdditional v-if="mode === 'timeline' && show.data.audio" />
        </b-col>
      </b-row>
      <b-row v-if="safeForAllModes">
        <b-col>
          <ShowTimelineLowerGrid v-if="mode === 'timeline'" />
        </b-col>
      </b-row>

      <ObjectPrint
        v-show="!newTimelineMode"
        title="state.show.currentShow"
        :data="$store.state.show.currentShow"
      />
      <ObjectPrint
        v-show="!newTimelineMode"
        title="state.show.slideIndex"
        :data="$store.state.show.slideIndex"
      />
    </b-container>

    <div
      v-show="mouseMoving"
      :style="overlayShapeStyle"
      class="overlay-shape-preview-rect"
    />
  </div>
</template>

<script>
import { db } from "@/fstore";
import cloneDeep from "lodash/cloneDeep";
import dragged from "@/directives/v-dragged/directive";
import ImporterAdditional from "@/components/show/ImporterAdditional";
import ImporterInitial from "@/components/show/ImporterInitial";
import LivePreviewNoAudio from "@/components/show/LivePreviewNoAudio";
import ModeBar from "@/components/show/ModeBar";
import ObjectPrint from "@/components/ObjectPrint";
import OverlayInspector from "@/components/show/inspectors/OverlayInspector";
import QuickKeys from "@/components/show/QuickKeys";
import ShowEmbed from "@/components/show/ShowEmbed";
import ShowExport from "@/components/show/ShowExport";
import ShowOverlays from "@/components/show/ShowOverlays";
import ShowSettings from "@/components/show/ShowSettings";
import ShowTimelineExists from "@/components/show/ShowTimelineExists";
import ShowTimelineLowerGrid from "@/components/show/ShowTimelineLowerGrid";
import ShowTimelineSetup from "@/components/show/ShowTimelineSetup";
import SlideInspector from "@/components/show/inspectors/SlideInspector";
import SoundslidesPlayer from "@/components/SoundslidesPlayer/SoundslidesPlayer";
import throttle from "lodash/throttle";
import Timeline from "@/components/show/timeline/Timeline";
import TopBar from "@/components/show/TopBar";
import WorldIcon from "@/components/WorldIcon";

export default {
  name: "ShowPlayer",
  components: {
    ImporterAdditional,
    ImporterInitial,
    LivePreviewNoAudio,
    ModeBar,
    ObjectPrint,
    OverlayInspector,
    QuickKeys,
    ShowEmbed,
    ShowExport,
    ShowOverlays,
    ShowSettings,
    ShowTimelineExists,
    ShowTimelineLowerGrid,
    ShowTimelineSetup,
    SlideInspector,
    SoundslidesPlayer,
    Timeline,
    TopBar,
    WorldIcon
  },
  directives: {
    dragged: dragged
  },
  data() {
    return {
      unbindFunction: () => {},
      playerMounted: false,
      windowWidth: 100,
      windowHeight: 100,
      throttledRefreshPlayerData: null,
      throttledOnResize: null,
      vResizerSplitPercent: 0.5,
      vResizerSplitDragPx: 0,
      vResizerHeight: 10,
      currentTime: 0,
      mouseDownStartObj: {},
      mouseMoveObj: {},
      mouseMoving: false
    };
  },
  computed: {
    mode() {
      return this.$route.params.mode || "timeline";
    },
    dataBound() {
      return this.$store.state.show.currentShowDataBound;
    },
    show() {
      return this.$store.state.show.currentShow;
    },
    refreshId() {
      return this.$store.state.show.refreshId;
    },
    displayInitialImporter() {
      return this.$store.getters["show/displayInitialImporter"];
    },
    minimalMediaDeployed() {
      return this.$store.getters["show/minimalMediaDeployed"];
    },
    isCurrentShowTimelineEdited() {
      return this.$store.getters["show/isCurrentShowTimelineEdited"];
    },
    safeForAllModes() {
      return !this.displayInitialImporter && this.minimalMediaDeployed;
    },
    showHasAudio() {
      return this.$store.getters["show/showHasAudio"];
    },
    newTimelineMode() {
      return this.mode == "waveform";
    },
    audioUrl() {
      var source_id = this.show.data.audio.source_id;
      var filename = this.show.data.audio.filename;
      return `https://cdn3.soundslides.com/audio/${source_id}/${filename}`;
    },
    displaySettings() {
      return this.$store.state.show.currentShow.data.displaySettings || {};
    },
    embedContainerStyle() {
      var _style = {};
      var padding = 100 / this.displaySettings.aspectRatio || 66.63;
      _style["padding-bottom"] = padding + "%";
      if (this.timelineFocusLayout) {
        var aspectRatio = this.displaySettings.aspectRatio || 1.5;

        var controlsHeight = 50;
        var newHeight = this.windowHeight * this.vResizerSplitPercent;
        var newWidth = (newHeight - controlsHeight) * aspectRatio;

        _style.width = newWidth + "px";
        _style.height = newHeight + "px";
        _style["padding-bottom"] = 0;
      }
      return _style;
    },
    timelineStyle() {
      var _style = {};
      if (this.timelineFocusLayout && "editTimeline" in this.$refs) {
        //var timelineHeight = this.$refs.editTimeline.$el.clientHeight;
        _style.width = this.windowWidth + "px";
        _style.top =
          this.windowHeight * this.vResizerSplitPercent +
          this.vResizerHeight +
          "px";
      }
      return _style;
    },
    vResizerStyle() {
      var _style = {};
      if (this.timelineFocusLayout && "editTimeline" in this.$refs) {
        _style.width = this.windowWidth + "px";
        _style.top = this.windowHeight * this.vResizerSplitPercent + "px";
      }
      return _style;
    },
    upperRightPaneStyle() {
      var _style = {};
      // Borrows calculations from the player.
      var aspectRatio = this.displaySettings.aspectRatio || 1.5;
      var controlsHeight = 50;
      var newHeight = this.windowHeight * this.vResizerSplitPercent;
      var newWidth = (newHeight - controlsHeight) * aspectRatio;
      _style.height = newHeight + "px";
      _style.width = this.windowWidth - newWidth + "px";
      _style.left = newWidth + "px";
      return _style;
    },
    slideArray() {
      if (!this.dataBound) {
        return [];
      }
      return this.show.data.slideArray;
    },
    timelineFocusLayout() {
      return this.$store.state.show.timelineFocusLayout;
    },
    activeItemType() {
      return (this.$store.state.show.activeItem || {}).type;
    },
    captureAndEmitClick() {
      if (this.$store.state.overlays.overlay_tool_value == "move") {
        return false;
      }
      return true;
    },
    overlayShapeStyle() {
      if (!("evt" in this.mouseDownStartObj)) return;
      var startEvt = this.mouseDownStartObj.evt;
      var moveEvt = this.mouseMoveObj;
      var left;
      if (moveEvt.clientX > startEvt.clientX) {
        left = startEvt.clientX;
      } else {
        left = moveEvt.clientX;
      }
      var top;
      if (moveEvt.clientY > startEvt.clientY) {
        top = startEvt.clientY;
      } else {
        top = moveEvt.clientY;
      }
      var _style = {
        left: left + "px",
        top: top + "px",
        width: Math.abs(moveEvt.clientX - startEvt.clientX) + "px",
        height: Math.abs(moveEvt.clientY - startEvt.clientY) + "px"
      };
      var tool_type = this.$store.state.overlays.overlay_tool_value;
      if (tool_type == "circle") {
        _style.borderRadius = "50%";
      }
      return _style;
    }
  },
  watch: {
    slideArray(value) {
      if (value.length > 0 && this.playerMounted) {
        // Instead of using a throttle, just refresh manually when emitted from components.
        //this.throttledRefreshPlayerData();
      }
    },
    timelineFocusLayout() {
      this.$nextTick(this.$refs.editPlayer.setDimensionValues);
      this.$nextTick(this.$refs.editTimeline.onResize);
    },
    refreshId() {
      this.refreshPlayerData();
    },
    activeItemType(value, old_value) {
      console.log("activeItemType", value);
      if (value == old_value) return;
      var _route = this.$route;
      if (value == "overlay" && _route.params.mode != "overlays") {
        _route.params.mode = "overlays";
        console.log(
          "$router.push('overlays') -- > disabling route change for now!!!"
        );
        //this.$router.push(_route);
      }
    }
  },
  beforeDestroy() {
    // Unbind.
    this.unbindFunction();
    this.$store.dispatch("show/unloadShow");
    window.removeEventListener("resize", this.throttledOnResize);
  },
  mounted() {
    var id = this.$route.params.id;
    console.log("Show", id);
    this.$store.dispatch("setBowser"); // May be unset.
    var _v = this;
    _v.unbindFunction = db
      .collection("editor_shows")
      .doc(id)
      .onSnapshot(
        function(doc) {
          console.log("Current data: ", doc.data());
          _v.$store.dispatch("show/setCurrentShow", doc.data());
          _v.$store.dispatch("show/updateCredentialsIfNeeded");
        },
        function(error) {
          console.log(error);
        }
      );

    this.throttledRefreshPlayerData = throttle(this.refreshPlayerData, 1000);
    this.throttledOnResize = throttle(this.onResize, 20);
    window.addEventListener("resize", this.throttledOnResize);
    this.onResize();
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
      if (!this.timelineFocusLayout && "editTimeline" in this.$refs) {
        // Trigger a resize of the non-focused timeline view.
        var playerY = this.$refs.editPlayer.$el.getBoundingClientRect().y;
        var timelineY = this.$refs.editTimeline.$el.getBoundingClientRect().y;
        this.$refs.editTimeline.checkToFitHeight(
          this.windowHeight - timelineY + playerY
        );
      }
    },
    onPlayerMounted() {
      this.playerMounted = true;
    },
    slideChange(value) {
      this.$store.commit("show/setSlideIndex", value);
    },
    timeUpdate(value) {
      this.currentTime = value;
    },
    gotoAndPause(time) {
      console.log("gotoAndPause", time);
      this.$refs.editPlayer.gotoAndPause(time);
    },
    onDeleteKey() {
      console.log("onDeleteKey");
      // Is there an active overlay item and is it actually editing active (highlighted).
      if (this.activeItemType == "overlay") {
        var activeItem = this.$store.state.show.activeItem;
        console.log("we have an overlay to delete!");
        if (
          this.$refs.editPlayer.$refs["overlay_" + activeItem.id][0].editing
        ) {
          var overlayConfirm = confirm(
            "Are you sure you want to remove this overlay?"
          );
          if (overlayConfirm) {
            this.$store.dispatch("overlays/removeOverlayById", activeItem.id);
          }
        }
      }
    },
    refreshPlayerData() {
      console.log("refreshPlayerData");
      var _slideArray = cloneDeep(
        this.$store.state.show.currentShow.data.slideArray
      );
      var _overlays = cloneDeep(
        this.$store.state.show.currentShow.data.overlayArray
      );
      var _settings = cloneDeep(
        this.$store.state.show.currentShow.data.displaySettings
      );
      if ("editPlayer" in this.$refs) {
        this.$refs.editPlayer.setSlideData(_slideArray, _overlays, _settings);
      }
    },
    onVResizerDragged(e) {
      if (e.first) {
        this.vResizerSplitDragPx =
          this.windowHeight * this.vResizerSplitPercent;
      }
      if (!e.first) {
        if (e.offsetY > 0 || e.offsetY < 0) {
          var newSplitPx = this.vResizerSplitDragPx + e.offsetY;
          this.vResizerSplitPercent = newSplitPx / this.windowHeight;
        }
        // Trigger resize event for the player.
        this.$nextTick(this.$refs.editPlayer.setDimensionValues);
        // Trigger a resize of the focused timeline view.
        this.$nextTick(function() {
          this.$refs.editTimeline.checkToFitHeight(
            this.windowHeight * (1 - this.vResizerSplitPercent)
          );
        });
      }
    },
    clickCaptured(obj) {
      console.log("clickCaptured", obj);
      var overlayPretransitionTime = 1;
      var inpoint =
        this.currentTime > overlayPretransitionTime
          ? this.currentTime - overlayPretransitionTime
          : this.currentTime;
      var tool_type = this.$store.state.overlays.overlay_tool_value;
      // Text overlay.
      if (tool_type == "text") {
        console.log("Creating new text overlay at ", obj.x, obj.y);
        this.$store.dispatch("overlays/addNewTextOverlay", {
          x: obj.x,
          y: obj.y,
          inpoint: inpoint
        });
        this.$store.commit("overlays/setOverlayToolValue", "move");
      }
      // Rect overlay
      if (tool_type == "rect" || tool_type == "circle") {
        console.log(`Ending new ${tool_type} overlay at `, obj.x, obj.y);
        var left;
        if (obj.x > this.mouseDownStartObj.x) {
          left = this.mouseDownStartObj.x;
        } else {
          left = obj.x;
        }
        var top;
        if (obj.y > this.mouseDownStartObj.y) {
          top = this.mouseDownStartObj.y;
        } else {
          top = obj.y;
        }
        var width = Math.abs(obj.x - this.mouseDownStartObj.x);
        var height = Math.abs(obj.y - this.mouseDownStartObj.y);
        var rect_obj = {
          x: left,
          y: top,
          width: width,
          height: height,
          inpoint: inpoint
        };
        if (obj.x < this.mouseDownStartObj.x) {
          rect_obj.x = obj.x;
        }
        if (obj.y < this.mouseDownStartObj.y) {
          rect_obj.y = obj.y;
        }

        console.log("rect_obj", rect_obj);
        if (tool_type == "rect") {
          this.$store.dispatch("overlays/addNewRectOverlay", rect_obj);
        }
        if (tool_type == "circle") {
          this.$store.dispatch("overlays/addNewCircleOverlay", rect_obj);
        }
        this.$store.commit("overlays/setOverlayToolValue", "move");
      }
      this.mouseMoving = false;
      this.$el.removeEventListener("mousemove", this.trackMouseMove);
    },
    mouseDownCaptured(obj) {
      console.log("mouseDownCaptured", obj);
      var tool_type = this.$store.state.overlays.overlay_tool_value;
      if (tool_type == "rect" || tool_type == "circle") {
        console.log("Starting new shape overlay at ", obj.x, obj.y);
        this.mouseDownStartObj = obj;
        this.$el.addEventListener("mousemove", this.trackMouseMove);
      }
    },
    trackMouseMove(e) {
      this.mouseMoving = true;
      this.mouseMoveObj = e;
    }
  }
};
</script>

<style lang="scss" scoped>
.embed-container {
  position: relative;
  padding: 0;
  //padding-bottom: 66.63%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
}
.absolute {
  position: absolute;
}
.absolute-row {
  position: fixed;
  top: 0px;
  left: 0px;
  //background-color: orange;
}
.absolute-player {
  top: 0px;
  left: 0px;
  background-color: pink;
}
.v-resizer {
  position: absolute;
  background-color: #cfcfcf;
  height: 10px;
}
.fullscreen-timeline {
  .row {
    position: fixed;
    margin: 0;
    padding-left: 0;
    left: 0px;
  }
  .col {
    position: absolute;
    margin: 0;
    padding-left: 20px;
    padding-right: 20px;
    //background-color: black;
    left: 0px;
  }
}
.upper-right-pane {
  position: absolute;
  top: 0px;
  padding: 10px;
  background: white;
  z-index: -1;
  overflow-x: hidden;
}
.overlay-shape-preview-rect {
  position: absolute;
  pointer-events: none;
  width: 100px;
  height: 100px;
  background-color: rgba(50, 205, 50, 0.4);
  border: dashed 1px white;
}
</style>
